import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { releaseNotesApi, ReleaseNote } from '../../utils/api';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import { INSTANT_FORMATS } from '../../utils/dateUtils';
import { OFFLINE_ENABLED } from '../../offline';
import { isTransportError } from '../../offline/connectivity';

interface WhatsNewDialogProps {
  open: boolean;
  onClose: () => void;
}

const WhatsNewDialog: React.FC<WhatsNewDialogProps> = ({ open, onClose }) => {
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorSeverity, setErrorSeverity] = useState<'error' | 'info'>('error');
  const [selectedNote, setSelectedNote] = useState<ReleaseNote | null>(null);

  useEffect(() => {
    if (open) {
      fetchReleaseNotes();
    }
  }, [open]);

  const fetchReleaseNotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const notes = await releaseNotesApi.getAllPublic();
      setReleaseNotes(notes);
    } catch (err: any) {
      console.error('Error fetching release notes:', err);
      // Offline mode: a transport failure (no response at all) gets neutral
      // framing instead of a scary red "Network Error" alert.
      if (OFFLINE_ENABLED && isTransportError(err)) {
        setErrorSeverity('info');
        setError("Release notes need an internet connection — try again when you're back online.");
      } else {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to load release notes';
        setErrorSeverity('error');
        setError(`Failed to load release notes: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewNote = (note: ReleaseNote) => {
    setSelectedNote(note);
  };

  const handleCloseViewer = () => {
    setSelectedNote(null);
  };

  // ReleaseNote.createdAt is an INSTANT (`@default(now())` — DATE-KINDS.md 1.4):
  // rendered, never date-parted, and in the CLINIC's zone so every seat in the
  // practice reads the same publication date.
  const formatInstant = useFormatInstant();
  const formatDate = (dateString: string) => formatInstant(dateString, 'MMMM d, yyyy');

  const handleClose = () => {
    setSelectedNote(null);
    onClose();
  };

  if (selectedNote) {
    return (
      <ReleaseNoteViewerDialog
        open={true}
        releaseNote={selectedNote}
        onClose={handleCloseViewer}
        onBack={() => setSelectedNote(null)}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h5" component="div">
          🎉 What's New
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity={errorSeverity} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Browse through all the latest updates and improvements to StableTrack
            </Typography>

            <List sx={{ width: '100%' }}>
              {releaseNotes.map((note) => (
                <ListItem
                  key={note.id}
                  disablePadding
                  sx={{
                    mb: 1,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemButton
                    onClick={() => handleViewNote(note)}
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Chip
                          label={`v${note.version}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          {formatDate(note.createdAt)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        {note.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {note.body.replace(/[#*_~`]/g, '').substring(0, 150)}...
                      </Typography>
                    </Box>
                    <LaunchIcon color="action" />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>

            {releaseNotes.length === 0 && !loading && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  No release notes available yet.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Individual Release Note Viewer Dialog
interface ReleaseNoteViewerDialogProps {
  open: boolean;
  releaseNote: ReleaseNote;
  onClose: () => void;
  onBack: () => void;
}

const ReleaseNoteViewerDialog: React.FC<ReleaseNoteViewerDialogProps> = ({
  open,
  releaseNote,
  onClose,
  onBack,
}) => {
  // Same INSTANT, same clinic zone as the list this dialog opened from.
  const formatInstant = useFormatInstant();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="h5" component="div" gutterBottom>
            {releaseNote.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Version {releaseNote.version} •{' '}
            {formatInstant(releaseNote.createdAt, INSTANT_FORMATS.dateNumeric)}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box
          sx={{
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              color: 'primary.main',
              mt: 2,
              mb: 1,
            },
            '& h1': {
              fontSize: '1.75rem',
              fontWeight: 600,
            },
            '& h2': {
              fontSize: '1.5rem',
              fontWeight: 600,
            },
            '& h3': {
              fontSize: '1.25rem',
              fontWeight: 600,
            },
            '& p': {
              mb: 1.5,
              lineHeight: 1.6,
            },
            '& ul, & ol': {
              pl: 3,
              mb: 1.5,
            },
            '& li': {
              mb: 0.5,
            },
            '& code': {
              backgroundColor: 'grey.100',
              color: 'error.main',
              px: 0.5,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.875rem',
              fontFamily: 'monospace',
            },
            '& pre': {
              backgroundColor: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              '& code': {
                backgroundColor: 'transparent',
                color: 'inherit',
                px: 0,
                py: 0,
              },
            },
            '& blockquote': {
              borderLeft: 4,
              borderColor: 'primary.main',
              pl: 2,
              ml: 0,
              fontStyle: 'italic',
              color: 'text.secondary',
            },
            '& a': {
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            },
            '& strong': {
              fontWeight: 600,
            },
            '& table': {
              width: '100%',
              borderCollapse: 'collapse',
              mt: 1,
              mb: 2,
            },
            '& th, & td': {
              border: 1,
              borderColor: 'divider',
              p: 1,
              textAlign: 'left',
            },
            '& th': {
              backgroundColor: 'grey.50',
              fontWeight: 600,
            },
          }}
        >
          <ReactMarkdown>{releaseNote.body}</ReactMarkdown>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onBack} variant="outlined">
          ← Back to All Versions
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsNewDialog;
